import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, FileText, Users, Clock, Lock, AlertCircle, DollarSign, Scale, HelpCircle } from 'lucide-react';
export default function Terms() {
  const [openSections, setOpenSections] = useState({
    definitions: true,
    instruction: true,
    duties: true,
    services: true,
    fees: true,
    timelines: true,
    dataProtection: true,
    intellectual: true,
    cancellation: true,
    liability: true,
    complaints: true,
    governing: true,
    amendments: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-816">
      <Helmet data-id="element-817">
        <title data-id="element-818">Terms & Conditions | Medicolegal Psychiatry</title>
        <meta name="description" content="Terms and conditions for Medicolegal Psychiatry services, outlining service agreements, obligations, data protection, and fees." data-id="element-819" />
        <meta name="keywords" content="Terms and Conditions, Service Agreement, Expert Witness Terms, Medicolegal Psychiatry" data-id="element-820" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-821">
        <div className="container mx-auto px-4" data-id="element-822">
          <div className="flex items-center justify-center mb-4" data-id="element-823">
            <Scale className="h-12 w-12 mr-4" data-id="element-824" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-825">
              Terms & Conditions
            </h1>
          </div>
        </div>
      </section>
      {/* Terms Content */}
      <section className="py-12" data-id="element-826">
        <div className="container mx-auto px-4" data-id="element-827">
          <div className="max-w-3xl mx-auto" data-id="element-828">
            <p className="text-slate-dark text-lg mb-8" data-id="element-829">
              These Terms and Conditions govern the provision of medico-legal
              psychiatric services by Dr. Imtiaz Syed (operating as "Medicolegal
              Psychiatry") to the instructing party. By instructing Dr. Syed,
              you are agreeing to these terms.
            </p>
            <div className="space-y-4 mb-8" data-id="element-830">
              {/* Definitions Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-831">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.definitions ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('definitions')} data-id="element-832">
                  <div className="flex items-center" data-id="element-833">
                    <FileText className="h-5 w-5 mr-2" data-id="element-834" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-835">
                      1. Definitions
                    </h2>
                  </div>
                  {openSections.definitions ? <ChevronUp className="h-5 w-5" data-id="element-836" /> : <ChevronDown className="h-5 w-5" data-id="element-837" />}
                </button>
                {openSections.definitions && <div className="p-6 bg-white" data-id="element-838">
                    <ul className="space-y-2 text-slate-dark" data-id="element-839">
                      <li data-id="element-840">
                        <strong data-id="element-841">"Dr. Syed"</strong> refers to Dr. Imtiaz Syed,
                        operating as Medicolegal Psychiatry.
                      </li>
                      <li data-id="element-842">
                        <strong data-id="element-843">"Instructing Party"</strong> refers to the
                        solicitor, law firm, court, tribunal, or other entity
                        that instructs Dr. Syed.
                      </li>
                      <li data-id="element-844">
                        <strong data-id="element-845">"Subject"</strong> refers to the individual who
                        is to be assessed by Dr. Syed.
                      </li>
                      <li data-id="element-846">
                        <strong data-id="element-847">"Services"</strong> refers to the medico-legal
                        psychiatric assessments, report writing, and expert
                        witness testimony provided by Dr. Syed.
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Instruction Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-848">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.instruction ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('instruction')} data-id="element-849">
                  <div className="flex items-center" data-id="element-850">
                    <FileText className="h-5 w-5 mr-2" data-id="element-851" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-852">
                      2. Instruction and Acceptance
                    </h2>
                  </div>
                  {openSections.instruction ? <ChevronUp className="h-5 w-5" data-id="element-853" /> : <ChevronDown className="h-5 w-5" data-id="element-854" />}
                </button>
                {openSections.instruction && <div className="p-6 bg-white" data-id="element-855">
                    <p className="text-slate-dark mb-4" data-id="element-856">
                      All instructions must be provided in writing, typically
                      via email to enquiries@medicolegal-psychiatry.co.uk. Dr.
                      Syed reserves the right to decline any instruction at his
                      discretion, particularly if there is a conflict of
                      interest or if the instruction falls outside his areas of
                      expertise.
                    </p>
                    <p className="text-slate-dark" data-id="element-857">
                      Upon acceptance of an instruction, Dr. Syed will provide a
                      Service Agreement outlining the specific services to be
                      provided, estimated timeframes, and fee structure. Work
                      will commence once this agreement has been signed and
                      returned by the Instructing Party.
                    </p>
                  </div>}
              </div>
              {/* Expert's Duties Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-858">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.duties ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('duties')} data-id="element-859">
                  <div className="flex items-center" data-id="element-860">
                    <Scale className="h-5 w-5 mr-2" data-id="element-861" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-862">
                      3. Expert's Duties
                    </h2>
                  </div>
                  {openSections.duties ? <ChevronUp className="h-5 w-5" data-id="element-863" /> : <ChevronDown className="h-5 w-5" data-id="element-864" />}
                </button>
                {openSections.duties && <div className="p-6 bg-white" data-id="element-865">
                    <p className="text-slate-dark" data-id="element-866">
                      Dr. Syed acknowledges and accepts the overriding duty to
                      the court as set out in the Civil Procedure Rules (CPR
                      Part 35), Family Procedure Rules (FPR Part 25), or
                      Criminal Procedure Rules (CrPR Part 19) as applicable. All
                      assessments and reports will be conducted with
                      impartiality, objectivity, and independence, regardless of
                      the instructing party.
                    </p>
                  </div>}
              </div>
              {/* Services Provided Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-867">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.services ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('services')} data-id="element-868">
                  <div className="flex items-center" data-id="element-869">
                    <Users className="h-5 w-5 mr-2" data-id="element-870" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-871">
                      4. Services Provided
                    </h2>
                  </div>
                  {openSections.services ? <ChevronUp className="h-5 w-5" data-id="element-872" /> : <ChevronDown className="h-5 w-5" data-id="element-873" />}
                </button>
                {openSections.services && <div className="p-6 bg-white" data-id="element-874">
                    <p className="text-slate-dark mb-4" data-id="element-875">
                      Services may include, but are not limited to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-876">
                      <li data-id="element-877">
                        Review of documentation provided by the Instructing
                        Party
                      </li>
                      <li data-id="element-878">
                        Psychiatric assessment of the Subject (remote or
                        in-person)
                      </li>
                      <li data-id="element-879">Preparation of medico-legal reports</li>
                      <li data-id="element-880">Attendance at conferences with counsel</li>
                      <li data-id="element-881">
                        Expert witness testimony in court or tribunal
                        proceedings
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-882">
                      The specific services to be provided will be detailed in
                      the Service Agreement.
                    </p>
                  </div>}
              </div>
              {/* Fees and Payment Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-883">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.fees ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('fees')} data-id="element-884">
                  <div className="flex items-center" data-id="element-885">
                    <DollarSign className="h-5 w-5 mr-2" data-id="element-886" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-887">
                      5. Fees and Payment
                    </h2>
                  </div>
                  {openSections.fees ? <ChevronUp className="h-5 w-5" data-id="element-888" /> : <ChevronDown className="h-5 w-5" data-id="element-889" />}
                </button>
                {openSections.fees && <div className="p-6 bg-white" data-id="element-890">
                    <p className="text-slate-dark mb-4" data-id="element-891">
                      Fees are calculated based on the complexity of the case,
                      volume of documentation, type of assessment required, and
                      estimated time involved. The fee structure will be
                      outlined in the Service Agreement and may include:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-892">
                      <li data-id="element-893">
                        Hourly rates for document review, report writing, and
                        conferences
                      </li>
                      <li data-id="element-894">Fixed fees for assessments</li>
                      <li data-id="element-895">Daily rates for court attendance</li>
                      <li data-id="element-896">Travel expenses and time (where applicable)</li>
                      <li data-id="element-897">
                        Cancellation fees (as per our Cancellation Policy)
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-898">
                      Payment terms are 30 days from the date of invoice. For
                      Legal Aid cases, Dr. Syed will work within the approved
                      funding limits, provided these are agreed upon in advance.
                    </p>
                  </div>}
              </div>
              {/* Timelines and Delivery */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-899">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.timelines ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('timelines')} data-id="element-900">
                  <div className="flex items-center" data-id="element-901">
                    <Clock className="h-5 w-5 mr-2" data-id="element-902" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-903">
                      6. Timelines and Delivery
                    </h2>
                  </div>
                  {openSections.timelines ? <ChevronUp className="h-5 w-5" data-id="element-904" /> : <ChevronDown className="h-5 w-5" data-id="element-905" />}
                </button>
                {openSections.timelines && <div className="p-6 bg-white" data-id="element-906">
                    <p className="text-slate-dark mb-4" data-id="element-907">
                      Dr. Syed aims to deliver reports within 10 working days of
                      the assessment, subject to the complexity of the case and
                      volume of documentation. Expedited reports (within 5
                      working days) may be available by prior arrangement and
                      may incur additional fees.
                    </p>
                    <p className="text-slate-dark" data-id="element-908">
                      While every effort will be made to meet agreed deadlines,
                      Dr. Syed cannot be held liable for delays resulting from
                      factors outside his control, such as the late provision of
                      documentation or the Subject's non-attendance.
                    </p>
                  </div>}
              </div>
              {/* Confidentiality and Data Protection */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-909">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataProtection ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataProtection')} data-id="element-910">
                  <div className="flex items-center" data-id="element-911">
                    <Lock className="h-5 w-5 mr-2" data-id="element-912" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-913">
                      7. Confidentiality and Data Protection
                    </h2>
                  </div>
                  {openSections.dataProtection ? <ChevronUp className="h-5 w-5" data-id="element-914" /> : <ChevronDown className="h-5 w-5" data-id="element-915" />}
                </button>
                {openSections.dataProtection && <div className="p-6 bg-white" data-id="element-916">
                    <p className="text-slate-dark mb-4" data-id="element-917">
                      Dr. Syed will handle all information in accordance with
                      the UK General Data Protection Regulation (UK GDPR), the
                      Data Protection Act 2018, and relevant professional
                      guidelines. Please refer to our Privacy Policy for
                      detailed information on how we collect, use, and protect
                      personal data.
                    </p>
                    <p className="text-slate-dark" data-id="element-918">
                      The Instructing Party is responsible for obtaining
                      appropriate consent from the Subject for the sharing of
                      their personal data with Dr. Syed for the purpose of
                      assessment and report preparation.
                    </p>
                  </div>}
              </div>
              {/* Intellectual Property */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-919">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.intellectual ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('intellectual')} data-id="element-920">
                  <div className="flex items-center" data-id="element-921">
                    <FileText className="h-5 w-5 mr-2" data-id="element-922" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-923">
                      8. Intellectual Property
                    </h2>
                  </div>
                  {openSections.intellectual ? <ChevronUp className="h-5 w-5" data-id="element-924" /> : <ChevronDown className="h-5 w-5" data-id="element-925" />}
                </button>
                {openSections.intellectual && <div className="p-6 bg-white" data-id="element-926">
                    <p className="text-slate-dark" data-id="element-927">
                      All reports and documents prepared by Dr. Syed remain his
                      intellectual property. The Instructing Party is granted a
                      license to use these documents for the specific legal
                      proceedings for which they were commissioned. Any other
                      use requires Dr. Syed's written permission.
                    </p>
                  </div>}
              </div>
              {/* Cancellation and Rescheduling */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-928">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.cancellation ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('cancellation')} data-id="element-929">
                  <div className="flex items-center" data-id="element-930">
                    <Clock className="h-5 w-5 mr-2" data-id="element-931" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-932">
                      9. Cancellation and Rescheduling
                    </h2>
                  </div>
                  {openSections.cancellation ? <ChevronUp className="h-5 w-5" data-id="element-933" /> : <ChevronDown className="h-5 w-5" data-id="element-934" />}
                </button>
                {openSections.cancellation && <div className="p-6 bg-white" data-id="element-935">
                    <p className="text-slate-dark" data-id="element-936">
                      Please refer to our separate Cancellation Policy for
                      detailed terms regarding the cancellation or rescheduling
                      of appointments and court attendance.
                    </p>
                  </div>}
              </div>
              {/* Liability */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-937">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.liability ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('liability')} data-id="element-938">
                  <div className="flex items-center" data-id="element-939">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-940" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-941">
                      10. Liability
                    </h2>
                  </div>
                  {openSections.liability ? <ChevronUp className="h-5 w-5" data-id="element-942" /> : <ChevronDown className="h-5 w-5" data-id="element-943" />}
                </button>
                {openSections.liability && <div className="p-6 bg-white" data-id="element-944">
                    <p className="text-slate-dark" data-id="element-945">
                      Dr. Syed maintains appropriate professional indemnity
                      insurance. His liability is limited to the fees paid for
                      the specific services provided. Dr. Syed is not liable for
                      any consequential, indirect, or special losses arising
                      from the provision of his services.
                    </p>
                  </div>}
              </div>
              {/* Complaints */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-946">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.complaints ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('complaints')} data-id="element-947">
                  <div className="flex items-center" data-id="element-948">
                    <HelpCircle className="h-5 w-5 mr-2" data-id="element-949" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-950">
                      11. Complaints
                    </h2>
                  </div>
                  {openSections.complaints ? <ChevronUp className="h-5 w-5" data-id="element-951" /> : <ChevronDown className="h-5 w-5" data-id="element-952" />}
                </button>
                {openSections.complaints && <div className="p-6 bg-white" data-id="element-953">
                    <p className="text-slate-dark" data-id="element-954">
                      If the Instructing Party is dissatisfied with any aspect
                      of Dr. Syed's services, they should first raise their
                      concerns directly with Dr. Syed. If the matter cannot be
                      resolved, the Instructing Party may refer to the relevant
                      professional body (e.g., General Medical Council, Royal
                      College of Psychiatrists) as appropriate.
                    </p>
                  </div>}
              </div>
              {/* Governing Law */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-955">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.governing ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('governing')} data-id="element-956">
                  <div className="flex items-center" data-id="element-957">
                    <Scale className="h-5 w-5 mr-2" data-id="element-958" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-959">
                      12. Governing Law
                    </h2>
                  </div>
                  {openSections.governing ? <ChevronUp className="h-5 w-5" data-id="element-960" /> : <ChevronDown className="h-5 w-5" data-id="element-961" />}
                </button>
                {openSections.governing && <div className="p-6 bg-white" data-id="element-962">
                    <p className="text-slate-dark" data-id="element-963">
                      These Terms and Conditions are governed by the laws of
                      England and Wales. Any disputes arising from these terms
                      shall be subject to the exclusive jurisdiction of the
                      courts of England and Wales.
                    </p>
                  </div>}
              </div>
              {/* Amendments */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-964">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.amendments ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('amendments')} data-id="element-965">
                  <div className="flex items-center" data-id="element-966">
                    <FileText className="h-5 w-5 mr-2" data-id="element-967" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-968">
                      13. Amendments
                    </h2>
                  </div>
                  {openSections.amendments ? <ChevronUp className="h-5 w-5" data-id="element-969" /> : <ChevronDown className="h-5 w-5" data-id="element-970" />}
                </button>
                {openSections.amendments && <div className="p-6 bg-white" data-id="element-971">
                    <p className="text-slate-dark" data-id="element-972">
                      Dr. Syed reserves the right to amend these Terms and
                      Conditions from time to time. The latest version will
                      always be available on our website. The version in effect
                      at the time of instruction will apply to that specific
                      case.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}